// DwsInput and the native pickers/selects: focus selection behaviour, auto-growing text areas, constraint
// validation, the client-side character filter and caret keeping, showPicker and select index helpers.
// Core: DwsInput is on almost every form.

import { publish } from "../shared/publish";

// Applies DwsInput's FocusSelectBehavior (select-all / cursor-to-start / cursor-to-end) when the
// field receives focus. Blazor has no built-in way to control text selection on focus.
function DwsInputApplyFocusBehavior(elementId: string, behavior: string): void {
    const element = document.getElementById(elementId) as HTMLInputElement | null;

    if (!element || typeof element.setSelectionRange !== 'function') {
        return;
    }

    let apply = function () {
        let length = element.value ? element.value.length : 0;

        // select() rather than setSelectionRange for select-all: a type="number" field throws on setSelectionRange, so
        // a numeric field never selected anything. Caret placement has no equivalent there and is skipped.
        try {
            switch (behavior) {
                case 'SelectAll':
                    element.select();
                    break;
                case 'CursorToStart':
                    element.setSelectionRange(0, 0);
                    break;
                case 'CursorToEnd':
                    element.setSelectionRange(length, length);
                    break;
                default:
                    break;
            }
        } catch (e) {
            // InvalidStateError: this input type has no caret API.
        }
    };

    apply();

    // Focus by mouse: the button is still down, and its mouseup puts the caret where the pointer is - undoing the
    // selection, so select-all only ever worked from the keyboard. Apply once more after that mouseup.
    if (element.matches(':active')) {
        let reapply = function () {
            element.removeEventListener('mouseup', reapply);
            setTimeout(apply, 0);
        };

        element.addEventListener('mouseup', reapply);
    }
}

// Grows a DwsInput textarea to fit its content (Lines > 1, AutoResize="true"), up to an optional
// max height derived from a max line count.
function DwsInputAutoResizeTextarea(elementId: string, maxLines?: number): void {
    DwsInputResizeTextareaElement(document.getElementById(elementId), maxLines);
}

function DwsInputResizeTextareaElement(element: HTMLElement | null, maxLines?: number): void {
    if (!element) {
        return;
    }

    // Important: the library's textarea.form-control { height: auto !important } (needed against the floating-label
    // height) beat a plain inline height, so the field never grew. Only an inline !important outranks it.
    element.style.setProperty('height', 'auto', 'important');

    // scrollHeight covers the text and the padding; the height (border-box) also takes the borders. MaxLines counts text
    // lines, so the padding (the floating label sits in the top one) and borders come on top - without them the field
    // stopped growing about two lines early.
    let style = getComputedStyle(element);
    let borders = (parseFloat(style.borderTopWidth) || 0) + (parseFloat(style.borderBottomWidth) || 0);
    let padding = (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);
    let newHeight = element.scrollHeight + borders;

    if (maxLines) {
        let lineHeight = parseFloat(style.lineHeight) || 20;
        let maxHeight = lineHeight * maxLines + padding + borders;
        newHeight = Math.min(newHeight, maxHeight);
    }

    element.style.setProperty('height', newHeight + 'px', 'important');
}

// Attaches the auto-resize behaviour to a DwsInput textarea once, so it runs locally on every
// keystroke instead of costing a Blazor interop round trip per key.
function DwsInputEnableAutoResize(elementId: string, maxLines?: number): void {
    const element = document.getElementById(elementId);

    if (!element) {
        return;
    }

    if (element.__dwsAutoResize) {
        element.removeEventListener('input', element.__dwsAutoResize);
    }

    let handler = function () { DwsInputResizeTextareaElement(element, maxLines); };

    element.__dwsAutoResize = handler;
    element.addEventListener('input', handler);

    handler();
}

function DwsInputDisableAutoResize(elementId: string): void {
    const element = document.getElementById(elementId);

    if (element && element.__dwsAutoResize) {
        element.removeEventListener('input', element.__dwsAutoResize);
        element.__dwsAutoResize = null;
    }
}

function ValidateEmailInput(elementId: string): boolean {
    const inputElement = document.getElementById(elementId) as HTMLInputElement | null;

    if (inputElement) {
        return inputElement.checkValidity();
    }
    return false;
}

// Tel twin of ValidateEmailInput. Unlike type="email", a bare type="tel" has no built-in format
// constraint in any browser, so checkValidity() here only reports false when the caller supplied a
// `pattern` (or `required`/`minlength`/...) through AdditionalAttributes. That is on purpose: no
// single phone regex is correct across locales, so the component does not invent one.
function ValidateTelInput(elementId: string): boolean {
    const inputElement = document.getElementById(elementId) as HTMLInputElement | null;

    if (inputElement) {
        return inputElement.checkValidity();
    }
    return false;
}

// Validates a VALUE against the constraints the given element carries, rather than validating the
// element itself. DwsInput calls this on blur, after it has applied its blur transforms (TrimOnBlur,
// CapitalizeOnBlur, BlurTransform, AutoCorrectMap) in C#. Those transforms have not reached the DOM at
// that point - Blazor re-renders only once the event handler completes - so checking the element would
// judge the text the user typed instead of the text the component actually holds, and a transform that
// repairs an address would still be reported invalid.
//
// A detached probe is used so nothing mutates the live element behind Blazor's back.
function DwsInputValidateValue(elementId: string, value: string | null): boolean {
    const source = document.getElementById(elementId) as HTMLInputElement | null;

    if (!source) {
        return false;
    }

    let probe = document.createElement('input');

    probe.type = source.type || 'text';

    let constraints = ['pattern', 'required', 'minlength', 'maxlength', 'min', 'max', 'step'];

    for (let i = 0; i < constraints.length; i++) {
        if (source.hasAttribute(constraints[i])) {
            probe.setAttribute(constraints[i], source.getAttribute(constraints[i]));
        }
    }

    probe.value = value == null ? '' : value;

    return probe.checkValidity();
}

function ShowPicker(elementId: string): void {
    const element = document.getElementById(elementId) as HTMLInputElement | null;

    // WebKit throws ("Invalid element type") where it has no picker of its own for the input, and older Safari has no
    // showPicker at all; the tap has focused the field already, so the input itself is the fallback.
    if (element && typeof element.showPicker === 'function') {
        try {
            element.showPicker();
        } catch (e) {
            // No picker available: leave the focused field as it is.
        }
    }
}

function SetSelectIndex(elementId: string, index: number): void {
    const element = document.getElementById(elementId) as HTMLSelectElement | null;

    if (element) {
        element.selectedIndex = index;
    }
}

function ResetSelectIndex(elementId: string): void {
    const element = document.getElementById(elementId) as HTMLSelectElement | null;

    if (element) {
        // Prefer DwsSelect's hidden empty option: with none selected, the browser selects the first option again as
        // soon as another option is added.
        const empty = element.querySelector<HTMLOptionElement>('option[data-dws-select-empty]');

        if (empty) {
            empty.selected = true;
        } else {
            element.selectedIndex = -1;
        }
    }
}

function GetSelectIndex(elementId: string): number {
    const element = document.getElementById(elementId) as HTMLSelectElement | null;

    if (!element) {
        return -1;
    }

    // DwsSelect's hidden empty option (no value) is not a real selection.
    const option = element.selectedIndex >= 0 ? element.options[element.selectedIndex] : null;

    if (option && option.hasAttribute('data-dws-select-empty')) {
        return -1;
    }

    return element.selectedIndex;
}

// DwsInput AllowedCharactersPattern and Uppercase/Lowercase, applied in the browser as well as on the server. Done only
// server-side, a refused keystroke showed until the round trip removed it - and keys typed meanwhile were tested
// against that stale text and lost too ("12a34" left "12") - while a re-cased value written back by the server moved
// the caret to the end. Here a refused keystroke never lands, and re-casing keeps the caret where it was. The server
// still checks everything; pattern null and transform '' detach the filter.
interface DwsClientFilter {
    regex: RegExp | null;
    transform: (text: string) => string;
    active: boolean;
}

function DwsInputSetClientFilter(elementId: string, pattern: string | null, transform: string | null, culture?: string | null): void {
    const element = document.getElementById(elementId) as HTMLInputElement | null;

    if (!element) {
        return;
    }

    let regex = null;

    if (pattern) {
        try {
            regex = new RegExp(pattern);
        } catch (e) {
            // A .NET-only construct: the server alone enforces it, as before.
            regex = null;
        }
    }

    const applyTransform = (text: string): string => {
        if (transform === 'upper') {
            return culture ? text.toLocaleUpperCase(culture) : text.toUpperCase();
        }

        if (transform === 'lower') {
            return culture ? text.toLocaleLowerCase(culture) : text.toLowerCase();
        }

        return text;
    };

    element.__dwsClientFilter = { regex: regex, transform: applyTransform, active: !!regex || !!transform } satisfies DwsClientFilter;

    if (element.__dwsClientFilterInstalled) {
        return;
    }

    element.__dwsClientFilterInstalled = true;

    element.addEventListener('beforeinput', function (e: InputEvent) {
        const filter = element.__dwsClientFilter as DwsClientFilter;

        if (!filter || !filter.regex || e.isComposing || !e.inputType || e.inputType.indexOf('insert') !== 0) {
            return;
        }

        let inserted = e.data;

        if (inserted === null || inserted === undefined) {
            inserted = e.dataTransfer ? e.dataTransfer.getData('text/plain') : '';
        }

        if (e.inputType === 'insertLineBreak' || e.inputType === 'insertParagraph') {
            inserted = '\n';
        }

        let value = element.value || '';
        let start = typeof element.selectionStart === 'number' ? element.selectionStart : value.length;
        let end = typeof element.selectionEnd === 'number' ? element.selectionEnd : value.length;
        let next = filter.transform(value.slice(0, start) + (inserted || '') + value.slice(end));

        if (!filter.regex.test(next)) {
            e.preventDefault();
        }
    });

    element.addEventListener('input', function () {
        const filter = element.__dwsClientFilter as DwsClientFilter;

        if (!filter || !filter.active) {
            return;
        }

        let value = element.value || '';
        let transformed = filter.transform(value);

        if (transformed === value) {
            return;
        }

        let start = element.selectionStart;
        let end = element.selectionEnd;

        element.value = transformed;

        // Case mapping can change the length ("ß" upper-cases to "SS"), so the caret is only put back when the length is unchanged.
        if (typeof start === 'number' && transformed.length === value.length) {
            element.setSelectionRange(start, end);
        }
    });
}

// DwsInput formats that rewrite more than the typed character (TitleCase, a custom formatter) come back from the server
// as a new value; setting it put the caret at the end. While the field has focus, a new value keeps the caret after the
// same number of letters and digits it had before, so separators the formatter adds or moves do not count. Installed by
// overriding this one element's value setter, which is what Blazor assigns.
function DwsInputKeepCaretOnRewrite(elementId: string): void {
    const element = document.getElementById(elementId) as HTMLInputElement | null;

    if (!element || element.__dwsKeepCaret) {
        return;
    }

    let proto = element instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
    let native = Object.getOwnPropertyDescriptor(proto, 'value');

    if (!native || !native.get || !native.set) {
        return;
    }

    let significant = /[A-Za-z0-9À-￿]/;
    element.__dwsKeepCaret = true;

    Object.defineProperty(element, 'value', {
        configurable: true,
        get: function () {
            return native.get.call(this);
        },
        set: function (next) {
            let previous = native.get.call(this);
            let caret = null;

            try {
                caret = this.selectionStart;
            } catch (e) {
                caret = null;
            }

            native.set.call(this, next);

            if (document.activeElement !== this || typeof caret !== 'number' || previous === next || typeof next !== 'string') {
                return;
            }

            let count = 0;

            for (let i = 0; i < caret && i < previous.length; i++) {
                if (significant.test(previous.charAt(i))) {
                    count++;
                }
            }

            let position = 0;

            while (position < next.length && count > 0) {
                if (significant.test(next.charAt(position))) {
                    count--;
                }

                position++;
            }

            try {
                this.setSelectionRange(position, position);
            } catch (e) {
                // Not a text field after all (type changed): nothing to restore.
            }
        }
    });
}

publish("DwsInputApplyFocusBehavior", DwsInputApplyFocusBehavior);
publish("DwsInputAutoResizeTextarea", DwsInputAutoResizeTextarea);
publish("DwsInputEnableAutoResize", DwsInputEnableAutoResize);
publish("DwsInputDisableAutoResize", DwsInputDisableAutoResize);
publish("ValidateEmailInput", ValidateEmailInput);
publish("ValidateTelInput", ValidateTelInput);
publish("DwsInputValidateValue", DwsInputValidateValue);
publish("ShowPicker", ShowPicker);
publish("SetSelectIndex", SetSelectIndex);
publish("ResetSelectIndex", ResetSelectIndex);
publish("GetSelectIndex", GetSelectIndex);
publish("DwsInputSetClientFilter", DwsInputSetClientFilter);
publish("DwsInputKeepCaretOnRewrite", DwsInputKeepCaretOnRewrite);
